import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Package, History, Layers, BookUser, Settings, Search, Bell, LogOut } from 'lucide-react';
import BrandMark from './BrandMark';
import { useSessionAction } from './SessionActionContext';

/** Route path → breadcrumb / page label. */
const PAGE_LABELS: Record<string, string> = {
  '/': 'Create shipment',
  '/orders': 'Orders',
  '/bulk-ship': 'Bulk ship',
  '/addresses': 'Address book',
  '/settings': 'Settings',
};

const navCls = ({ isActive }: { isActive: boolean }) =>
  isActive ? 'nav-item active' : 'nav-item';

/** Two-letter avatar initials from a display name, falling back to the email. */
function initialsFrom(name?: string, email?: string): string {
  const source = (name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return source.slice(0, 2).toUpperCase();
  }
  const address = (email || '').trim();
  if (address) return address.slice(0, 2).toUpperCase();
  return 'F4';
}

function Layout() {
  const session = useSessionAction();
  const location = useLocation();
  const crumb = PAGE_LABELS[location.pathname] ?? 'Console';
  const avatarText = initialsFrom(session?.userName, session?.userEmail);

  return (
    <div className="app">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Sidebar — brand, navigation, and the operator footer */}
      <nav className="sidebar" aria-label="Main navigation">
        <div className="sidebar-brand">
          <BrandMark size={27} className="brand-mark" />
          <div>
            <div className="brand-word">FIFTY 410</div>
            <div className="brand-sub">Shipping console</div>
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-label">Shipping</div>
          <NavLink to="/" end className={navCls}>
            <Package size={15} strokeWidth={1.75} />
            <span>Create shipment</span>
          </NavLink>
          <NavLink to="/orders" className={navCls}>
            <History size={15} strokeWidth={1.75} />
            <span>Orders</span>
          </NavLink>
          <NavLink to="/bulk-ship" className={navCls}>
            <Layers size={15} strokeWidth={1.75} />
            <span>Bulk ship</span>
          </NavLink>

          <div className="nav-label">Manage</div>
          <NavLink to="/addresses" className={navCls}>
            <BookUser size={15} strokeWidth={1.75} />
            <span>Address book</span>
          </NavLink>
          <NavLink to="/settings" className={navCls}>
            <Settings size={15} strokeWidth={1.75} />
            <span>Settings</span>
          </NavLink>
        </div>

        <div className="sidebar-foot">
          <div className="sidebar-status">
            <span className="live-dot" />
            FedEx account · live
          </div>
          {session && (
            <div className="sidebar-user">
              <span className="user-avatar" aria-hidden="true">
                {avatarText}
              </span>
              <div className="user-meta">
                <div className="user-name">{session.userName || 'Operator'}</div>
                {session.userEmail && <div className="user-email">{session.userEmail}</div>}
              </div>
              <button
                type="button"
                className="user-signout"
                onClick={session.onAction}
                disabled={session.disabled}
                title={session.actionTitle || session.actionLabel}
                aria-label={session.actionLabel}
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Right column — breadcrumb topbar + scrollable content */}
      <div className="app-col">
        <header className="topbar">
          <div className="crumb">
            <span className="crumb-root">Fifty 410</span>
            <span className="crumb-sep">/</span>
            <b>{crumb}</b>
          </div>

          <div className="top-search">
            <div>
              <Search size={13} aria-hidden="true" />
              <span style={{ flex: 1 }}>Search shipments, tracking, recipients…</span>
              <kbd>⌘K</kbd>
            </div>
          </div>

          <span className="env-badge success">
            <span
              style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--success-dot)' }}
            />
            Production
          </span>
          <span className="topbar-sep" />
          <button type="button" className="icon-btn" aria-label="Notifications">
            <Bell size={15} strokeWidth={1.75} />
          </button>
          <span className="avatar" aria-hidden="true">
            {avatarText}
          </span>
        </header>

        {/* Routed pages render here */}
        <main id="main-content" className="app-main">
          <div className="page">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
